import { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { rsvpData } from '../mockData';
import { Heart, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { AdditionalGuestFields } from '../components/rsvp/AdditionalGuestFields';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RSVP = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    attending: '',
    attendingDays: [],
    additionalGuests: [],
    bringingKids: false,
    numberOfKids: 0,
    dietaryRestrictions: '',
    dietaryDetails: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const currentDays = prev.attendingDays || [];
      const newDays = currentDays.includes(day)
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      return {
        ...prev,
        attendingDays: newDays
      };
    });
  };

  const handleAddGuest = () => {
    setFormData(prev => ({
      ...prev,
      additionalGuests: [
        ...prev.additionalGuests,
        { _uid: crypto.randomUUID(), firstName: '', lastName: '', email: '', phone: '' }
      ]
    }));
  };

  const handleRemoveGuest = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalGuests: prev.additionalGuests.filter((_, i) => i !== index)
    }));
  };

  const handleAdditionalGuestChange = (index, field, value) => {
    setFormData(prev => {
      const newGuests = [...prev.additionalGuests];
      newGuests[index] = {
        ...newGuests[index],
        [field]: value
      };
      return {
        ...prev,
        additionalGuests: newGuests
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Validation: If attending, at least one day must be selected
    if (formData.attending === 'yes' && (!formData.attendingDays || formData.attendingDays.length === 0)) {
      setError(t('rsvp.selectOneDay'));
      setLoading(false);
      return;
    }
    
    // Validation: Check additional guests have required fields
    if (formData.additionalGuests.length > 0) {
      for (let i = 0; i < formData.additionalGuests.length; i++) {
        const guest = formData.additionalGuests[i];
        if (!guest.firstName || !guest.lastName || !guest.email) {
          setError(`${t('rsvp.guestNumber')} ${i + 2}: ${t('rsvp.firstName')}, ${t('rsvp.lastName')}, ${t('rsvp.email')}`);
          setLoading(false);
          return;
        }
      }
    }
    
    try {
      await axios.post(`${API_URL}/api/rsvp`, {
        ...formData,
        additionalGuests: formData.additionalGuests.map(({ _uid, ...guest }) => guest)
      });
      
      setSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          attending: '',
          attendingDays: [],
          additionalGuests: [],
          bringingKids: false,
          numberOfKids: 0,
          dietaryRestrictions: '',
          dietaryDetails: '',
          message: ''
        });
      }, 5000);
    } catch (err) {
      // Check if it's a duplicate email error
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already exists')) {
        setError('This email has already been used for an RSVP. If you need to update your response, please contact Polina & Evgeniy directly.');
      } else {
        setError('Failed to submit RSVP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-4 pt-20">
          <Card className="max-w-md w-full border-2 border-primary shadow-xl cream-bg">
            <CardContent className="pt-12 pb-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 blue-text" />
              </div>
              <h2 className="font-serif text-3xl font-bold text-foreground mb-4">
                {t('rsvp.thankYou')}
              </h2>
              <p className="font-sans text-lg text-muted-foreground mb-2">
                {t('rsvp.successMessage')}
              </p>
              <p className="font-sans text-base text-muted-foreground mb-2">
                {t('rsvp.successMessage2')}
              </p>
              <p className="font-sans text-sm blue-text opacity-80 mt-4 mb-8">
                {t('rsvp.confirmationEmail')}
              </p>
              
              {/* WhatsApp Channel Button */}
              <div className="max-w-md mx-auto mt-8">
                <a 
                  href="https://whatsapp.com/channel/0029VbBclMVBqbrIScXb9X3Q"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="border-2 border-primary cream-bg p-4 hover:blue-bg hover:text-white transition-all duration-300 group">
                    <div className="flex items-center justify-center gap-3">
                      {/* WhatsApp Icon */}
                      <svg 
                        className="w-6 h-6 flex-shrink-0 blue-text group-hover:text-white transition-colors" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      
                      {/* Text */}
                      <p className="font-sans text-sm font-semibold blue-text group-hover:text-white transition-colors">
                        {t('whatsapp.joinChannel')}
                      </p>
                      
                      {/* Arrow */}
                      <svg 
                        className="w-4 h-4 flex-shrink-0 blue-text group-hover:text-white transition-all group-hover:translate-x-1" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </a>
              </div>
              
              <Heart className="w-8 h-8 blue-text fill-blue-200 mx-auto mt-6 animate-pulse" />
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-48 pb-16 cream-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-in">
          <Heart className="w-12 h-12 blue-text mx-auto mb-6" />
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-semibold blue-text mb-6">
            {t('rsvp.title')}
          </h1>
          <p className="font-sans text-lg md:text-xl blue-text opacity-80">
            {t('rsvp.subtitle')}
          </p>
        </div>
      </section>

      {/* RSVP Form */}
      <section className="py-20 cream-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-primary shadow-xl cream-bg">
            <CardHeader className="cream-bg">
              <CardTitle className="font-serif text-2xl blue-text">
                {t('rsvp.guestInfo')}
              </CardTitle>
              <CardDescription className="blue-text opacity-80">
                {t('rsvp.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8 cream-bg">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="font-sans font-medium blue-text">
                      {t('rsvp.firstName')} *
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      autoComplete="given-name"
                      required
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      className="border-2 border-primary focus:border-primary focus:ring-primary cream-bg"
                      placeholder={t('rsvp.firstName')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="font-sans font-medium blue-text">
                      {t('rsvp.lastName')} *
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      autoComplete="family-name"
                      required
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      className="border-2 border-primary focus:border-primary focus:ring-primary cream-bg"
                      placeholder={t('rsvp.lastName')}
                    />
                  </div>
                </div>

                {/* Contact Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-sans font-medium blue-text">
                      {t('rsvp.email')} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="border-2 border-primary focus:border-primary focus:ring-primary cream-bg"
                      placeholder={t('rsvp.email')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-sans font-medium blue-text">
                      {t('rsvp.phone')}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="border-2 border-primary focus:border-primary focus:ring-primary cream-bg"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                {/* Attendance */}
                <div className="space-y-2">
                  <Label htmlFor="attending" className="font-sans font-medium blue-text">
                    {t('rsvp.attendance')} *
                  </Label>
                  <Select 
                    required
                    value={formData.attending}
                    onValueChange={(value) => handleChange('attending', value)}
                  >
                    <SelectTrigger className="border-2 border-primary focus:border-primary focus:ring-primary cream-bg">
                      <SelectValue placeholder={t('rsvp.attendance')} />
                    </SelectTrigger>
                    <SelectContent className="cream-bg">
                      <SelectItem value="yes">{t('rsvp.attendingYes')}</SelectItem>
                      <SelectItem value="no">{t('rsvp.attendingNo')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.attending === 'yes' && (
                  <>
                    {/* Which Days Attending */}
                    <div className="space-y-3">
                      <Label className="font-sans font-medium blue-text">
                        {t('rsvp.whichDays')} *
                      </Label>
                      <div className="space-y-3 p-4 border-2 border-primary cream-bg">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id="day1"
                            checked={formData.attendingDays?.includes('day1')}
                            onCheckedChange={() => handleDayToggle('day1')}
                            className="border-primary"
                          />
                          <label
                            htmlFor="day1"
                            className="font-sans text-sm cursor-pointer flex-1"
                          >
                            {t('rsvp.day1')}
                          </label>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id="day2"
                            checked={formData.attendingDays?.includes('day2')}
                            onCheckedChange={() => handleDayToggle('day2')}
                            className="border-primary"
                          />
                          <label
                            htmlFor="day2"
                            className="font-sans text-sm cursor-pointer flex-1"
                          >
                            {t('rsvp.day2')}
                          </label>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id="day3"
                            checked={formData.attendingDays?.includes('day3')}
                            onCheckedChange={() => handleDayToggle('day3')}
                            className="border-primary"
                          />
                          <label
                            htmlFor="day3"
                            className="font-sans text-sm cursor-pointer flex-1"
                          >
                            {t('rsvp.day3')}
                          </label>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t('rsvp.selectOneDay')}
                      </p>
                    </div>

                    {/* Add Guest Button */}
                    <div className="border-t-2 border-primary pt-6">
                      <Button
                        type="button"
                        onClick={handleAddGuest}
                        className="blue-bg text-white hover:opacity-90 transition-opacity"
                      >
                        {t('rsvp.addGuest')}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('rsvp.addGuestNote')}
                      </p>
                    </div>

                    {/* Additional Guests Fields */}
                    {formData.additionalGuests.map((guest, index) => (
                      <AdditionalGuestFields
                        key={guest._uid}
                        guest={guest}
                        index={index}
                        onChange={handleAdditionalGuestChange}
                        onRemove={handleRemoveGuest}
                      />
                    ))}

                    {/* Bringing Kids Section */}
                    <div className="space-y-3 border-t-2 border-primary pt-6">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="bringingKids"
                          checked={formData.bringingKids}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              bringingKids: checked,
                              numberOfKids: checked ? prev.numberOfKids : 0
                            }));
                          }}
                          className="border-primary"
                        />
                        <label
                          htmlFor="bringingKids"
                          className="font-sans font-medium blue-text cursor-pointer"
                        >
                          {t('rsvp.bringingKids')}
                        </label>
                      </div>
                      
                      {formData.bringingKids && (
                        <div className="ml-8 space-y-2">
                          <Label htmlFor="numberOfKids" className="font-sans font-medium blue-text">
                            {t('rsvp.numberOfKids')}
                          </Label>
                          <Input
                            id="numberOfKids"
                            type="number"
                            min="1"
                            max="10"
                            value={formData.numberOfKids || ''}
                            onChange={(e) => handleChange('numberOfKids', parseInt(e.target.value) || 0)}
                            className="border-2 border-primary focus:border-primary focus:ring-primary cream-bg w-32"
                            placeholder="0"
                          />
                        </div>
                      )}
                    </div>

                    {/* Dietary Restrictions */}
                    <div className="space-y-2">
                      <Label htmlFor="dietaryRestrictions" className="font-sans font-medium blue-text">
                        {t('rsvp.dietary')}
                      </Label>
                      <Select 
                        value={formData.dietaryRestrictions}
                        onValueChange={(value) => handleChange('dietaryRestrictions', value)}
                      >
                        <SelectTrigger className="border-2 border-primary focus:border-primary focus:ring-primary cream-bg">
                          <SelectValue placeholder={t('rsvp.dietary')} />
                        </SelectTrigger>
                        <SelectContent className="cream-bg">
                          <SelectItem value="none">{t('rsvp.dietaryNone')}</SelectItem>
                          <SelectItem value="vegetarian">{t('rsvp.dietaryVegetarian')}</SelectItem>
                          <SelectItem value="vegan">{t('rsvp.dietaryVegan')}</SelectItem>
                          <SelectItem value="gluten-free">{t('rsvp.dietaryGlutenFree')}</SelectItem>
                          <SelectItem value="other">{t('rsvp.dietaryOther')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.dietaryRestrictions === 'other' && (
                      <div className="space-y-2">
                        <Label htmlFor="dietaryDetails" className="font-sans font-medium blue-text">
                          {t('rsvp.dietaryDetails')}
                        </Label>
                        <Input
                          id="dietaryDetails"
                          value={formData.dietaryDetails}
                          onChange={(e) => handleChange('dietaryDetails', e.target.value)}
                          className="border-2 border-primary focus:border-primary focus:ring-primary cream-bg"
                          placeholder={t('rsvp.dietaryDetails')}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="font-sans font-medium blue-text">
                    {t('rsvp.message')}
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="border-2 border-primary focus:border-primary focus:ring-primary min-h-32 cream-bg"
                    placeholder={t('rsvp.message')}
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-50 border-2 border-red-300 rounded-md">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-full blue-bg hover:opacity-90 text-white text-lg py-7 font-medium transition-opacity border-2 border-primary disabled:opacity-50"
                >
                  {loading ? t('rsvp.submitting') : t('rsvp.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};
